import {
  ActionMenu,
  ConfirmationDialog,
  DataGrid,
  Dialog,
  FilterToolbar,
  Form,
  Input,
  useNotifications,
  type FilterDef,
  type GridQuery
} from '@ijoin/components';
import {
  getGetAdminLoginHealthQueryKey,
  useDeleteAdminAccountsRefLogin,
  useDeleteAdminAccountsRefLogins,
  useDeleteAdminLogin,
  useGetAdminLoginHealth,
  usePostAdminLoginsPurgeOrphaned,
  type GetAdminLoginHealthParams,
  type LoginHealthRowRes,
  type LoginHealthRowResIssueCode
} from '@ijoin/hooks';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

import { camelCase, capitalize, sortBy } from 'lodash';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useToggle } from 'usehooks-ts';

import { LinkButton } from '../../components';
import { ApiError } from '../../types';

const ISSUE_LABELS: Record<string, string> = {
  accountLinkWithoutLogin: 'Account link without login',
  accountWithoutLogin: 'Account without login',
  loginWithoutAccount: 'Login without account links',
  refWithoutAccount: 'Link without account'
};

const ISSUE_DESCRIPTIONS: Record<string, string> = {
  accountLinkWithoutLogin:
    'Advisor, participant, or admin whose account links point at no login. Delete the links so a new login can be created.',
  accountWithoutLogin:
    'Advisor, participant, or admin with no login at all. Normal for participants who have never signed in.',
  loginWithoutAccount:
    'Login with no account links; it cannot access anything. Safe to purge.',
  refWithoutAccount:
    'Account link whose advisor, participant, or admin record no longer exists. Unlink to clean up.'
};

const ROW_ACTIONS: Partial<
  Record<
    LoginHealthRowResIssueCode,
    {
      confirmText: string;
      content: string;
      message: (row: LoginHealthRowRes) => string;
      title: string;
    }
  >
> = {
  accountLinkWithoutLogin: {
    confirmText: 'Delete',
    content: 'Delete Login Links',
    message: row =>
      `This removes all account-login links for "${row.firstName ?? ''} ${row.lastName ?? ''}" so a new login can be created for the account.`,
    title: 'Delete Login Links'
  },
  loginWithoutAccount: {
    confirmText: 'Delete',
    content: 'Delete Login',
    message: row => `This permanently deletes login "${row.username}".`,
    title: 'Delete Login'
  },
  refWithoutAccount: {
    confirmText: 'Unlink',
    content: 'Unlink',
    message: row =>
      `This removes the orphaned account link for login "${row.username}".`,
    title: 'Unlink Account'
  }
};

const LoginHealthTab = () => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();
  const [query, setQuery] = useState<GridQuery>();
  const [confirming, setConfirming] = useState<LoginHealthRowRes | null>(null);
  const [purgeOpen, , setPurgeOpen] = useToggle(false);
  const purgeConfirmMethods = useForm({ defaultValues: { confirmText: '' } });

  const loginHealth = useGetAdminLoginHealth(
    query as GetAdminLoginHealthParams,
    { query: { enabled: !!query, placeholderData: keepPreviousData } }
  );

  const invalidate = () => {
    queryClient.invalidateQueries({
      queryKey: getGetAdminLoginHealthQueryKey()
    });
  };

  const deleteLogin = useDeleteAdminLogin<ApiError>({
    mutation: {
      onError: error => notifications.error(error.message),
      onSuccess: () => {
        invalidate();
        notifications.success('Login deleted');
      }
    }
  });

  const unlinkAccount = useDeleteAdminAccountsRefLogin<ApiError>({
    mutation: {
      onError: error => notifications.error(error.message),
      onSuccess: () => {
        invalidate();
        notifications.success('Account link removed');
      }
    }
  });

  const deleteLoginLinks = useDeleteAdminAccountsRefLogins<ApiError>({
    mutation: {
      onError: error => notifications.error(error.message),
      onSuccess: data => {
        invalidate();
        notifications.success(`Removed ${data.deleted} login links`);
      }
    }
  });

  const purgeOrphanedLogins = usePostAdminLoginsPurgeOrphaned<ApiError>({
    mutation: {
      onError: error => notifications.error(error.message),
      onSuccess: data => {
        invalidate();
        notifications.success(`Purged ${data.purged} orphaned logins`);
      }
    }
  });

  const onCancelAction = () => setConfirming(null);

  const onConfirmAction = () => {
    if (!confirming) return;

    if (confirming.issueCode === 'loginWithoutAccount') {
      deleteLogin.mutate({ loginId: confirming.entityId });
    }

    if (confirming.issueCode === 'refWithoutAccount') {
      unlinkAccount.mutate({ accountsRefLoginsId: confirming.entityId });
    }

    if (confirming.issueCode === 'accountLinkWithoutLogin') {
      deleteLoginLinks.mutate({ params: { accountId: confirming.entityId } });
    }

    setConfirming(null);
  };

  const onOpenPurge = () => {
    purgeConfirmMethods.reset();
    setPurgeOpen(true);
  };

  const onPurgeClose = () => {
    setPurgeOpen(false);
    purgeConfirmMethods.reset();
  };

  const onConfirmPurge = () => {
    setPurgeOpen(false);
    purgeConfirmMethods.reset();
    purgeOrphanedLogins.mutate();
  };

  const filters: FilterDef[] = [
    {
      label: 'Issue Type',
      name: 'issueCode',
      options: [
        {
          label: 'Account link without login',
          value: 'accountLinkWithoutLogin'
        },
        { label: 'Account without login', value: 'accountWithoutLogin' },
        { label: 'Link without account', value: 'refWithoutAccount' },
        { label: 'Login without account links', value: 'loginWithoutAccount' }
      ],
      type: 'select'
    },
    {
      label: 'Account Type',
      name: 'accountType',
      options: [
        { label: 'Admins', value: 'admins' },
        { label: 'Advisors', value: 'advisors' },
        { label: 'Participants', value: 'participants' }
      ],
      type: 'select'
    },
    { label: 'First Name', name: 'firstName', type: 'text' },
    { label: 'Last Name', name: 'lastName', type: 'text' },
    { label: 'Username', name: 'username', type: 'text' }
  ];

  const columns: GridColDef<LoginHealthRowRes>[] = [
    {
      field: 'issueCode',
      flex: 1.5,
      headerName: 'Issue',
      sortable: false,
      valueGetter: value => ISSUE_LABELS[value] ?? value
    },
    {
      field: 'accountType',
      flex: 1,
      headerName: 'Account Type',
      sortable: false,
      valueGetter: value => capitalize(value ?? '')
    },
    {
      field: 'firstName',
      flex: 1,
      headerName: 'First Name'
    },
    {
      field: 'lastName',
      flex: 1,
      headerName: 'Last Name'
    },
    {
      field: 'username',
      flex: 1.5,
      headerName: 'Username',
      renderCell: params =>
        params.row.loginId ? (
          <LinkButton
            data-testid={camelCase(`loginHealthLogin ${params.value}`)}
            params={{ loginId: params.row.loginId }}
            to='/users/$loginId'
            variant='text'>
            {params.value}
          </LinkButton>
        ) : (
          (params.value ?? '')
        )
    },
    {
      field: 'advisoryOrgName',
      flex: 1.5,
      headerName: 'Advisory Org',
      renderCell: params =>
        params.row.advisoryOrganizationId ? (
          <LinkButton
            params={{
              advisoryOrganizationId: params.row.advisoryOrganizationId
            }}
            to='/organizations/$advisoryOrganizationId'
            variant='text'>
            {params.value ?? ''}
          </LinkButton>
        ) : (
          (params.value ?? '')
        ),
      sortable: false
    },
    {
      field: 'entityId',
      flex: 1.5,
      headerName: 'Record ID',
      sortable: false
    },
    {
      field: 'actions',
      headerName: 'Actions',
      renderCell: params =>
        ROW_ACTIONS[params.row.issueCode] ? (
          <ActionMenu
            actions={[
              {
                content: ROW_ACTIONS[params.row.issueCode]?.content,
                onSelect: () => setConfirming(params.row)
              }
            ]}
            trigger={
              <IconButton
                data-testid={`loginHealthActions-${params.row.entityId}`}>
                <MoreVertIcon />
              </IconButton>
            }
          />
        ) : null,
      sortable: false,
      width: 80
    }
  ];

  const confirmingAction = confirming && ROW_ACTIONS[confirming.issueCode];

  const issueInfo = (
    <Tooltip
      title={
        <Stack spacing={0.5}>
          {sortBy(Object.keys(ISSUE_LABELS)).map(code => (
            <Typography key={code} variant='body2'>
              <b>{ISSUE_LABELS[code]}:</b> {ISSUE_DESCRIPTIONS[code]}
            </Typography>
          ))}
        </Stack>
      }>
      <IconButton color='inherit'>
        <InfoOutlinedIcon data-testid='issueTypesInfo' />
      </IconButton>
    </Tooltip>
  );

  const toolbarActions = (
    <>
      {issueInfo}
      <Tooltip title='Purge Orphaned Logins'>
        <IconButton
          color='error'
          data-testid='purgeOrphanedLogins'
          disabled={purgeOrphanedLogins.isPending}
          onClick={onOpenPurge}>
          <DeleteSweepIcon />
        </IconButton>
      </Tooltip>
    </>
  );

  return (
    <Stack spacing={1} sx={{ flex: 1, minHeight: 0 }}>
      <DataGrid
        autoHeight={false}
        columns={columns}
        disableColumnMenu
        disableRowSelectionOnClick
        filterDefs={filters}
        getRowId={row => `${row.issueCode}:${row.entityId}`}
        initialSort={[{ field: 'lastName', sort: 'asc' }]}
        loading={!query || loginHealth.isFetching}
        onClear={setQuery}
        onSearch={setQuery}
        pageSize={25}
        pageSizeOptions={[10, 25, 50, 100]}
        paginationMode='server'
        rowCount={loginHealth.data?.pagination?.total ?? 0}
        rows={loginHealth.data?.data ?? []}
        showColumnMenu
        showToolbar
        slotProps={{
          root: {
            'data-component': 'dataGrid',
            'data-testid': 'loginHealth'
          },
          toolbar: {
            actions: toolbarActions,
            onRefresh: loginHealth.refetch,
            searchButtonTestId: 'search',
            triggerTestId: 'loginHealthFilterTrigger'
          }
        }}
        slots={{ toolbar: FilterToolbar }}
        sortingMode='server'
        sx={{ flex: 1, minHeight: 0 }}
      />

      <ConfirmationDialog
        confirmColor='error'
        confirmText={confirmingAction?.confirmText ?? 'Confirm'}
        data-testid='loginHealthConfirmDialog'
        isOpen={!!confirming}
        message={
          confirming && confirmingAction
            ? confirmingAction.message(confirming)
            : ''
        }
        onCancel={onCancelAction}
        onConfirm={onConfirmAction}
        title={confirmingAction?.title ?? ''}
      />

      <Dialog
        data-testid='purgeOrphanedLoginsDialog'
        isOpen={purgeOpen}
        onClose={onPurgeClose}>
        <Form methods={purgeConfirmMethods}>
          <DialogTitle variant='h3'>Purge Orphaned Logins</DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 2 }}>
              This permanently deletes every login not linked to any account,
              not just the logins listed here. Type &quot;purge&quot; to
              confirm.
            </Typography>
            <Input
              autoComplete='off'
              data-1p-ignore
              data-testid='purgeConfirmInput'
              format='string'
              fullWidth
              label="Type 'purge' to confirm"
              name='confirmText'
              size='small'
            />
          </DialogContent>
          <DialogActions>
            <Button
              data-testid='cancelPurge'
              onClick={onPurgeClose}
              variant='outlined'>
              Cancel
            </Button>
            <Button
              color='error'
              data-testid='confirmPurge'
              disabled={purgeConfirmMethods.watch('confirmText') !== 'purge'}
              onClick={onConfirmPurge}
              variant='contained'>
              Purge
            </Button>
          </DialogActions>
        </Form>
      </Dialog>
    </Stack>
  );
};

export const Route = createFileRoute('/relationship-health/login')({
  component: LoginHealthTab
});
